import type { BoardDependencyChip } from '@nightshift/ui-types';

import type { TicketState } from './palette.ts';

/** Commands the board may offer on a card. Drag issues the same set. */
export type BoardAction =
  | 'start'
  | 'record_done'
  | 'shelve'
  | 'hold'
  | 'cancel'
  | 'restore'
  | 'resume';
export type CardMenuAction = BoardAction | 'research' | 'planning' | 'deploy' | 'queue' | 'rescan';

const BOARD_ACTION_LABEL: Record<CardMenuAction, string> = {
  start: 'Start',
  research: 'Research',
  planning: 'Planning',
  record_done: 'Already done',
  shelve: 'Shelve',
  hold: 'Hold',
  cancel: 'Cancel',
  restore: 'Restore',
  resume: 'Resume',
  deploy: 'Deploy',
  queue: 'Queue',
  rescan: 'Rescan',
};

/** Short consequences shown when a destructive or state-changing menu action is hovered. */
export const BOARD_ACTION_HINT: Partial<Record<CardMenuAction, string>> = {
  shelve: 'Stop work and return this ticket to Backlog.',
  hold: 'Pause here. Resume later from this stage.',
  cancel: 'Stop work, move this ticket to Cancelled, and close its MR.',
};

/**
 * Legal commands for a card in this state.
 *
 * Start is Backlog's "begin work" and is also a visible button on the card. Already done
 * records historic Backlog work as Done without the pipeline. Shelve parks
 * an in-flight ticket. Hold is Waiting / Manual Hold. Cancel is available until Done.
 * Restore is Cancelled only. Resume wakes Waiting. Merge and plan-decision live in the
 * drawer, not on the card.
 */
export function boardActionsFor(state: TicketState): BoardAction[] {
  switch (state) {
    case 'Backlog':
      return ['start', 'record_done', 'cancel'];
    case 'Cancelled':
      return ['restore'];
    case 'Done':
      return [];
    case 'Waiting':
      return ['resume', 'cancel'];
    default:
      return ['shelve', 'hold', 'cancel'];
  }
}

export interface CardMenuOptions {
  isAdmin?: boolean;
  deployConfigured?: boolean;
  deployable?: boolean;
  /** The card's outstanding blockers, which is what Queue schedules the start behind. */
  dependsOn?: readonly BoardDependencyChip[];
}

/**
 * Card overflow / right-click menu. Deploy is admin-only on Done when the
 * project has a GitLab job name configured and the ticket has a merge SHA.
 * Historic Done (no SHA) never offers it.
 */
export function cardMenuActions(state: TicketState, options: CardMenuOptions = {}): CardMenuAction[] {
  const base = boardActionsFor(state);
  if (state === 'Backlog') return ['start', 'research', 'planning', 'record_done', 'cancel'];
  if (
    state === 'Done' &&
    options.isAdmin === true &&
    options.deployConfigured === true &&
    options.deployable === true
  ) {
    return ['deploy'];
  }
  return base;
}

/** One row of the card menu: a command, plus whatever that command needs naming. */
export interface CardMenuItem {
  action: CardMenuAction;
  label: string;
  hint?: string;
  /** Queue only: the blocker the park is recorded against. */
  blocker?: string;
}

/**
 * Blockers a start can still be scheduled behind.
 *
 * `satisfied` never reaches a card, and a cancelled blocker will never land — offering to
 * wait for one would be offering a wait that never ends, and the server refuses it anyway.
 */
function queueableBlockers(options: CardMenuOptions): BoardDependencyChip[] {
  return (options.dependsOn ?? []).filter(
    (row) => row.chip !== 'satisfied' && row.chip !== 'unavailable' && row.chip !== 'missing',
  );
}

/**
 * Queue: take this start later, on its own, once the work it depends on has landed.
 *
 * One entry however many blockers there are, because that is what the park means — it walks
 * from blocker to blocker as each lands and starts the ticket when the last one is Done. The
 * key named here is only where the wait begins.
 */
function queueMenuItem(state: TicketState, options: CardMenuOptions = {}): CardMenuItem | null {
  if (state !== 'Backlog') return null;
  const blockers = queueableBlockers(options);
  const first = blockers[0];
  if (!first) return null;
  const keys = blockers.map((row) => row.ticketKey);
  return {
    action: 'queue',
    label: keys.length === 1 ? `Queue behind ${keys[0]}` : `Queue behind ${keys.length} blockers`,
    hint:
      keys.length === 1
        ? `Starts on its own once ${keys[0]} is Done.`
        : `Starts on its own once ${keys.join(', ')} are Done.`,
    blocker: first.ticketKey,
  };
}

/**
 * The card menu as it is rendered: `cardMenuActions` plus the rows that carry a target.
 *
 * Queue sits with the other ways to begin the work rather than down by Cancel: it answers the
 * same question Start does, with "later, by itself" instead of "now".
 */
export function cardMenuItems(state: TicketState, options: CardMenuOptions = {}): CardMenuItem[] {
  const queue = queueMenuItem(state, options);
  const items: CardMenuItem[] = [];
  for (const action of cardMenuActions(state, options)) {
    if (queue && action === 'record_done') items.push(queue);
    items.push({
      action,
      label: BOARD_ACTION_LABEL[action],
      ...(BOARD_ACTION_HINT[action] ? { hint: BOARD_ACTION_HINT[action] } : {}),
    });
  }
  items.push({ action: 'rescan', label: 'Rescan', hint: 'Regenerate footprint, then dependencies. Reset failed retries.' });
  return items;
}
